import React, { useCallback, useEffect, useState } from 'react';
import { styled } from '@compiled/react';
import debounce from 'lodash/debounce';
import type { DocNode, DocNode as ADF } from '@atlaskit/adf-schema';
import {
	type EditorActions,
	Editor,
	WithEditorActions,
	EditorContext,
} from '@atlaskit/editor-core';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import { Field } from '@atlaskit/form';
import EditFilledIcon from '@atlaskit/icon/glyph/edit-filled';
import { ReactRenderer } from '@atlaskit/renderer';
import { useIntl } from '@atlassian/jira-intl';
import { smartLinksDefault } from '@atlassian/jira-linking-platform-utils';
import messages from './messages';

type Props = {
	content: DocNode | JSONDocNode;
	onDescriptionChange: (value: JSONDocNode) => void;
	saveTrigger: number;
};

export const Description = ({ content, onDescriptionChange, saveTrigger }: Props) => {
	const { formatMessage } = useIntl();
	const [isEditing, setIsEditing] = useState(false);

	useEffect(() => {
		setIsEditing(false);
	}, [saveTrigger]);

	const onChangeValue = useCallback(
		(actions: EditorActions<ADF>) =>
			debounce(() => {
				actions.getValue().then((value): void => {
					if (value !== undefined) {
						onDescriptionChange(value);
					}
				});
			}, 100),
		[onDescriptionChange],
	);

	const renderEditorInternal = useCallback(
		(actions: EditorActions<ADF>) => (
			<Editor defaultValue={content} onChange={onChangeValue(actions)} appearance="comment" />
		),
		[content, onChangeValue],
	);

	const renderEditor = useCallback(
		() => (
			<EditorContext>
				<WithEditorActions render={renderEditorInternal} />
			</EditorContext>
		),
		[renderEditorInternal],
	);

	const onEdit = useCallback(() => {
		setIsEditing(true);
	}, []);

	const readView = useCallback(() => {
		if (!content?.content.length) {
			return (
				<DescriptionWrapper>
					<InputWrapper
						type="text"
						onClick={onEdit}
						onKeyPress={onEdit}
						placeholder={formatMessage(messages.descriptionPlaceholderText)}
					/>
					<EditButtonWrapper
						onClick={onEdit}
						data-testid="project-details.form.description.project-details-description-edit-button"
					>
						<EditFilledIcon label={formatMessage(messages.editDescriptionLabel)} size="small" />
					</EditButtonWrapper>
				</DescriptionWrapper>
			);
		}

		return (
			<DescriptionWrapper onClick={onEdit} onKeyPress={onEdit} role="button" tabIndex={0}>
				<ReactRenderer
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					document={content as DocNode}
					smartLinks={smartLinksDefault}
				/>
				<EditButtonWrapper
					onClick={onEdit}
					data-testid="project-details.form.description.project-details-description-edit-button"
				>
					<EditFilledIcon label={formatMessage(messages.editDescriptionLabel)} size="small" />
				</EditButtonWrapper>
			</DescriptionWrapper>
		);
	}, [content, formatMessage, onEdit]);

	return (
		<Field name="description" label={formatMessage(messages.projectDescriptionLabel)}>
			{
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				({ fieldProps }) => (isEditing ? renderEditor() : readView())
			}
		</Field>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DescriptionWrapper = styled.div({
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InputWrapper = styled.input({
	border: 'none',
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EditButtonWrapper = styled.button({
	border: 'none',
	background: 'none',
	/* stylelint-disable-next-line */ // getting error Unexpected unknown property "role"   property-no-unknown
	role: 'button',
	cursor: 'pointer',
});
